/**
 * CloudBackupHint — small inline prompt shown inside the save-lecture
 * flow (LiveRecordingDialog + CreateLectureForm). When the user hasn't
 * connected a personal cloud (Google Drive / OneDrive / Dropbox) yet,
 * we surface a contextual nudge with a link to Settings. When they
 * already have at least one connected provider, we show a quiet
 * confirmation so they know backups are happening.
 *
 * Drive linkage UI lives ONLY in Settings (iter191 cleanup) — this
 * component does NOT itself initiate OAuth; it just points to Settings.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Cloud, ArrowRight, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CloudBackupHint = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/storage/providers`)
      .then(({ data }) => {
        if (cancelled) return;
        setProviders(data?.providers || []);
      })
      .catch(() => { /* silent — feature is optional */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  const connected = providers.filter((p) => p.connected);
  if (connected.length > 0) {
    const labels = connected.map((p) => p.label || p.id).join(', ');
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-600 dark:text-emerald-300"
        data-testid="cloud-backup-hint-connected"
      >
        <Check className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
        <span>Recordings will also back up to <strong>{labels}</strong>.</span>
      </div>
    );
  }

  return (
    <Link
      to="/settings#storage"
      className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-[#002FA7]/5 border border-[#002FA7]/20 text-xs hover:bg-[#002FA7]/10 transition-colors group"
      data-testid="cloud-backup-hint-prompt"
    >
      <div className="flex items-center gap-2 text-[var(--isp-body)]">
        <Cloud className="w-4 h-4 text-[#002FA7] flex-shrink-0" aria-hidden />
        <span>
          <strong className="text-[var(--isp-heading)]">Auto-backup lectures to Drive?</strong>
          <span className="hidden sm:inline"> Connect Google Drive or OneDrive in Settings.</span>
        </span>
      </div>
      <span className="flex items-center gap-1 text-[#002FA7] font-semibold whitespace-nowrap">
        Connect <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
};

export default CloudBackupHint;
